import React, { useEffect, useState } from "react";
import TechSoftFooter from "../../components/common/TechSoftFooter";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Typography,
  Chip,
  Paper,
  Divider,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  WorkOutline as WorkOutlineIcon,
  Login as LoginIcon,
  PersonAddAlt as PersonAddIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { buscarVagasPublicas } from "../../services/vaga";
import { Vaga } from "../../types";

// Página PÚBLICA de divulgação das vagas ativas (sem login).
// A Prefeitura pode divulgar o link /vagas em redes sociais.
const VagasPublicas: React.FC = () => {
  const navigate = useNavigate();

  // Sessão de candidato (mesmas chaves usadas pelo AuthContext/painel-vagas)
  const candidatoLogado = Boolean(
    sessionStorage.getItem("candidatoToken") &&
      sessionStorage.getItem("candidatoId"),
  );
  const candidatoNome = sessionStorage.getItem("candidatoNome") || "";

  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [termoPesquisa, setTermoPesquisa] = useState<string>("");

  // Estado da paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const vagasPorPagina = 10;

  // Estados para modal de detalhes da vaga
  const [modalDetalhes, setModalDetalhes] = useState(false);
  const [vagaSelecionada, setVagaSelecionada] = useState<Vaga | null>(null);

  useEffect(() => {
    const carregarVagas = async () => {
      const vagasData = await buscarVagasPublicas();
      setVagas(Array.isArray(vagasData) ? vagasData : []);
    };
    carregarVagas();
  }, []);

  // Sem sessão de candidato: direciona para o login/cadastro de candidato
  const irParaLoginCandidato = () => {
    navigate("/login-candidato");
  };

  // Funções para controlar o modal de detalhes
  const abrirModalDetalhes = (vaga: Vaga) => {
    if (!candidatoLogado) {
      irParaLoginCandidato();
      return;
    }
    setVagaSelecionada(vaga);
    setModalDetalhes(true);
  };

  const fecharModalDetalhes = () => {
    setModalDetalhes(false);
    setVagaSelecionada(null);
  };

  const handleCandidatar = () => {
    if (!candidatoLogado) {
      irParaLoginCandidato();
      return;
    }
    // Logado: segue o fluxo normal existente no painel de vagas
    navigate("/painel-vagas");
  };

  const vagasFiltradas =
    Array.isArray(vagas) && vagas.length > 0
      ? vagas.filter((vaga) => {
          const termo = termoPesquisa.toLowerCase();
          return (
            vaga.nomeVaga?.toLowerCase().includes(termo) ||
            vaga.cargo?.toLowerCase().includes(termo) ||
            vaga.descricaoVaga?.toLowerCase().includes(termo) ||
            vaga.nomeEmpresa?.toLowerCase().includes(termo)
          );
        })
      : [];

  const todasVagasOrdenadas =
    vagasFiltradas.length > 0
      ? vagasFiltradas.sort((a, b) => Number(b.id) - Number(a.id))
      : [];

  // Calcular vagas para a página atual
  const indiceInicial = (paginaAtual - 1) * vagasPorPagina;
  const indiceFinal = indiceInicial + vagasPorPagina;
  const vagasPaginadas = todasVagasOrdenadas.slice(indiceInicial, indiceFinal);
  const totalPaginas = Math.ceil(todasVagasOrdenadas.length / vagasPorPagina);

  // Resetar para página 1 quando o termo de pesquisa mudar
  useEffect(() => {
    setPaginaAtual(1);
  }, [termoPesquisa]);

  // Handler para mudança de página
  const handleMudancaPagina = (
    event: React.ChangeEvent<unknown>,
    novaPagina: number,
  ) => {
    setPaginaAtual(novaPagina);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Tema fixo claro para página pública (mesmo do painel de vagas)
  const lightTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#1976d2",
      },
      secondary: {
        main: "#dc004e",
      },
      background: {
        default: "#f5f5f5",
        paper: "#ffffff",
      },
    },
    typography: {
      fontFamily: "Montserrat, Inter, Arial, sans-serif",
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header com acesso ao login/cadastro */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {candidatoLogado && candidatoNome && (
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Olá, <strong>{candidatoNome}</strong>
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {candidatoLogado ? (
              <Button
                variant="contained"
                startIcon={<PersonIcon />}
                onClick={() => navigate("/painel-vagas")}
                sx={{
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.95rem",
                }}
              >
                Ir para o Painel de Vagas
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate("/login-candidato")}
                  sx={{
                    borderColor: "#1976d2",
                    color: "#1976d2",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    borderWidth: 2,
                    "&:hover": {
                      borderColor: "#1565c0",
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                      borderWidth: 2,
                    },
                  }}
                >
                  Entrar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate("/vagas")}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                  }}
                >
                  Cadastre-se
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{ mt: 2, mb: 2, textAlign: "center" }}
        >
          PAINEL DE VAGAS - SINE DE BOM RETIRO DO SUL
        </Typography>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 2, textAlign: "center" }}
        >
          VAGAS DISPONÍVEIS
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb: 3,
            textAlign: "center",
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          Mais recentes primeiro. Para se candidatar, faça login ou
          cadastre-se gratuitamente.
        </Typography>

        {/* Campo de Pesquisa */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, maxWidth: 1485, mx: "auto" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Pesquisar por vaga, cargo, descrição ou empresa..."
            value={termoPesquisa}
            onChange={(e) => setTermoPesquisa(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4a90e2",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4a90e2",
                  borderWidth: 2,
                },
              },
            }}
          />
          {termoPesquisa && (
            <Typography
              variant="body2"
              sx={{ mt: 2, color: "text.secondary", textAlign: "center" }}
            >
              {todasVagasOrdenadas.length} vaga(s) encontrada(s) para "
              {termoPesquisa}"
            </Typography>
          )}
        </Paper>

        {/* Informação de paginação */}
        {todasVagasOrdenadas.length > 0 && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, maxWidth: 1485, mx: "auto" }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Typography variant="body2" color="text.secondary">
                Mostrando {indiceInicial + 1} -{" "}
                {Math.min(indiceFinal, todasVagasOrdenadas.length)} de{" "}
                {todasVagasOrdenadas.length} vagas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Página {paginaAtual} de {totalPaginas}
              </Typography>
            </Stack>
          </Paper>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {vagasPaginadas.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ width: "100%", textAlign: "center", py: 4 }}
            >
              {termoPesquisa
                ? `Nenhuma vaga encontrada para "${termoPesquisa}". Tente pesquisar por outros termos.`
                : "Nenhuma vaga disponível no momento. Por favor, volte mais tarde."}
            </Typography>
          ) : (
            vagasPaginadas.map((vaga: Vaga) => (
              <Box
                key={`vaga-${vaga.id}`}
                sx={{
                  width: {
                    xs: "100%",
                    sm: "calc(50% - 12px)",
                    md: "calc(33.333% - 16px)",
                    lg: "calc(25% - 18px)",
                    xl: "calc(20% - 19.2px)",
                  },
                }}
              >
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    minHeight: 400,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      width: 160,
                      height: 160,
                      borderRadius: "12px",
                      background:
                        vaga.imagemEmpresa && vaga.imagemEmpresa.trim() !== ""
                          ? "#ffffff"
                          : "linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                      margin: "16px auto",
                      border: "2px solid #e0e0e0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  >
                    {vaga.imagemEmpresa && vaga.imagemEmpresa.trim() !== "" ? (
                      <img
                        src={vaga.imagemEmpresa}
                        alt={vaga.nomeEmpresa || "Empresa"}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          display: "block",
                          margin: "auto",
                        }}
                      />
                    ) : (
                      <WorkOutlineIcon
                        sx={{
                          fontSize: "3rem",
                          color: "#ffffff",
                          opacity: 0.9,
                          filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                        }}
                      />
                    )}
                  </Box>
                  <CardHeader
                    title={(() => {
                      const nomeEmpresa =
                        vaga.nomeEmpresa || "Empresa não identificada";
                      const willBeTruncated = nomeEmpresa.length > 25;

                      const typography = (
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{
                            fontSize:
                              nomeEmpresa.length > 30
                                ? 14
                                : nomeEmpresa.length > 20
                                ? 16
                                : nomeEmpresa.length > 15
                                ? 18
                                : 20,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            cursor: "default",
                          }}
                        >
                          {nomeEmpresa}
                        </Typography>
                      );

                      return willBeTruncated ? (
                        <Tooltip title={nomeEmpresa} placement="top">
                          {typography}
                        </Tooltip>
                      ) : (
                        typography
                      );
                    })()}
                    sx={{ pb: 0, minHeight: "64px", textAlign: "center" }}
                  />
                  <CardContent
                    sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        height: "40px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip
                        title={
                          (vaga.nomeVaga || "").length > 20 ? vaga.nomeVaga : ""
                        }
                        placement="top"
                      >
                        <Chip
                          label={vaga.nomeVaga}
                          sx={{
                            border: "2px solid #4a90e2",
                            backgroundColor: "#4a90e2",
                            color: "white",
                            fontWeight: "bold",
                            height: "32px",
                            fontSize:
                              (vaga.nomeVaga || "").length > 35
                                ? "0.65rem"
                                : (vaga.nomeVaga || "").length > 30
                                ? "0.7rem"
                                : (vaga.nomeVaga || "").length > 25
                                ? "0.75rem"
                                : (vaga.nomeVaga || "").length > 20
                                ? "0.8rem"
                                : "0.875rem",
                            cursor: "default",
                            "& .MuiChip-label": {
                              padding: "0 8px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "180px",
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {/* Botão Mais Informações */}
                    <Box
                      sx={{ mb: 2, display: "flex", justifyContent: "center" }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<InfoIcon />}
                        onClick={() => abrirModalDetalhes(vaga)}
                        sx={{
                          borderColor: "#1976d2",
                          color: "#1976d2",
                          fontWeight: 600,
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderWidth: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#1565c0",
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                            borderWidth: 1.5,
                            transform: "translateY(-1px)",
                          },
                          "&:active": {
                            transform: "translateY(0)",
                          },
                        }}
                      >
                        Mais Informações
                      </Button>
                    </Box>
                    <Tooltip
                      title={
                        (vaga.cargo || "").length > 25
                          ? `Cargo: ${vaga.cargo || "N/A"}`
                          : ""
                      }
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2,
                          fontSize:
                            (vaga.cargo || "").length > 25
                              ? "0.7rem"
                              : (vaga.cargo || "").length > 20
                              ? "0.75rem"
                              : (vaga.cargo || "").length > 15
                              ? "0.8rem"
                              : "0.875rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "default",
                          lineHeight: 1.2,
                        }}
                      >
                        Cargo: {vaga.cargo || "N/A"}
                      </Typography>
                    </Tooltip>
                    {vaga.dataCriacao && (
                      <Typography
                        variant="caption"
                        sx={{ mb: 2, color: "text.secondary" }}
                      >
                        Criada em: {vaga.dataCriacao}
                      </Typography>
                    )}
                    <Box sx={{ mt: "auto" }}>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={handleCandidatar}
                        sx={{
                          background:
                            "linear-gradient(135deg, #009688 0%, #26a69a 50%, #4db6ac 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #00796b 0%, #00897b 50%, #009688 100%)",
                          },
                          fontWeight: "bold",
                          boxShadow: "0 3px 8px rgba(0,150,136,0.4)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        Candidatar-se
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))
          )}
        </Box>

        {/* Componente de Paginação */}
        {todasVagasOrdenadas.length > vagasPorPagina && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={totalPaginas}
                  page={paginaAtual}
                  onChange={handleMudancaPagina}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      fontSize: "1rem",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#1976d2 !important",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#1565c0 !important",
                      },
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Mostrando {indiceInicial + 1} -{" "}
                  {Math.min(indiceFinal, todasVagasOrdenadas.length)} de{" "}
                  {todasVagasOrdenadas.length} vagas
                </Typography>
              </Stack>
            </Paper>
          </Box>
        )}

        <Paper elevation={1} sx={{ mt: 2, p: { xs: 2, sm: 4 } }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Mais Informações
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Atendimento:</strong>
            <br />
            Centro Administrativo
            <br />
            Av. Senador Pinheiro Machado, 35
            <br />
            Bairro Centro Cidade Baixa
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Horário de Atendimento:</strong>
            <br />
            Segunda a sexta-feira: 8h às 12h e das 13h às 17h
          </Typography>
          <Typography variant="body1">
            <strong>Telefone:</strong> (51) 3766-1255
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>E-mail:</strong> ind.comercio@bomretirodosul.rs.gov.br
          </Typography>

          <Divider sx={{ my: 2 }} />

          <TechSoftFooter />
        </Paper>

        {/* Modal de Detalhes da Vaga (apenas para candidato logado) */}
        <Dialog
          open={modalDetalhes}
          onClose={fecharModalDetalhes}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="primary">
              Detalhes da Vaga
            </Typography>
            <IconButton
              onClick={fecharModalDetalhes}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            {vagaSelecionada && (
              <Box>
                {/* Informações da Empresa */}
                <Paper
                  elevation={1}
                  sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        background:
                          vagaSelecionada.imagemEmpresa &&
                          vagaSelecionada.imagemEmpresa.trim() !== ""
                            ? "#ffffff"
                            : "linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        marginRight: 2,
                        border: "2px solid #e0e0e0",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }}
                    >
                      {vagaSelecionada.imagemEmpresa &&
                      vagaSelecionada.imagemEmpresa.trim() !== "" ? (
                        <img
                          src={vagaSelecionada.imagemEmpresa}
                          alt={vagaSelecionada.nomeEmpresa || "Empresa"}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                            margin: "auto",
                          }}
                        />
                      ) : (
                        <WorkOutlineIcon
                          sx={{
                            fontSize: "2rem",
                            color: "#ffffff",
                            opacity: 0.9,
                            filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {vagaSelecionada.nomeEmpresa ||
                          "Empresa não identificada"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Empresa Contratante
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Informações da Vaga */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 2, color: "primary" }}
                  >
                    Informações da Vaga
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 2,
                    }}
                  >
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Nome da Vaga
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vagaSelecionada.nomeVaga || "N/A"}
                      </Typography>
                    </Paper>

                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Cargo
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vagaSelecionada.cargo || "N/A"}
                      </Typography>
                    </Paper>

                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Salário
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vagaSelecionada.salario
                          ? `R$ ${vagaSelecionada.salario.toLocaleString(
                              "pt-BR",
                              { minimumFractionDigits: 2 },
                            )}`
                          : "A combinar"}
                      </Typography>
                    </Paper>

                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Data de Criação
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vagaSelecionada.dataCriacao || "N/A"}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>

                {/* Descrição da Vaga */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 2, color: "primary" }}
                  >
                    Descrição da Vaga
                  </Typography>
                  <Paper elevation={1} sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {vagaSelecionada.descricaoVaga ||
                        "Descrição não disponível."}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1, borderTop: "1px solid #e0e0e0" }}>
            <Button
              onClick={fecharModalDetalhes}
              variant="outlined"
              sx={{
                borderColor: "#1976d2",
                color: "#1976d2",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                borderWidth: 2,
                "&:hover": {
                  borderColor: "#1565c0",
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  borderWidth: 2,
                },
              }}
            >
              Fechar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                fecharModalDetalhes();
                handleCandidatar();
              }}
              sx={{
                background:
                  "linear-gradient(135deg, #009688 0%, #26a69a 50%, #4db6ac 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #00796b 0%, #00897b 50%, #009688 100%)",
                },
                fontWeight: "bold",
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 3px 8px rgba(0,150,136,0.4)",
                transition: "all 0.3s ease",
              }}
            >
              Candidatar-se
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default VagasPublicas;
